import { useCallback, useEffect, useState } from "react";
import { CalendarDays, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { DateField, isoDay } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";
import { shortDate } from "@/lib/format";

const STATUS_STYLE = {
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  submitted: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  manager_approved: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  rejected: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  cancelled: "border-border bg-muted text-muted-foreground",
};

const BLANK = {
  leave_type: "annual", from_date: isoDay(new Date()), to_date: isoDay(new Date()),
  reason: "", document_reference: "", contact_while_away: "",
};

export default function MyLeave() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/hr/leave/mine");
      setData(res.data);
    } catch (e) {
      setError(apiError(e));
      setData(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    let ok = false;
    try {
      const res = await fn();
      toast.success(typeof message === "function" ? message(res) : message);
      await load();
      ok = true;
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
    return ok;
  };

  const submit = async () => {
    const ok = await act("submit", () => api.post("/hr/leave/requests", form),
      (res) => `${res.data.leave_ref} sent to ${res.data.manager || "HR"} for a decision`);
    if (ok) setForm(null);
  };

  if (data === null) return <Loading label="Loading your leave" />;
  if (data === false) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="My leave" title="Leave and absence"
                    description="Request time off and follow where it is." />
        <Panel testId="panel-no-staff-record" title="No staff record">
          <p className="text-[13px] text-muted-foreground" data-testid="my-leave-error">{error}</p>
        </Panel>
      </div>
    );
  }

  const selected = data.types.find((t) => t.code === form?.leave_type);
  const balance = data.balances.find((b) => b.code === form?.leave_type);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={`My leave · ${data.staff.staff_ref}`}
        title="Leave and absence"
        description={`Your requests go to ${data.staff.manager || "HR"} for a decision. Anything longer than ${data.chair_escalation_days} working days also needs the President / Chairperson. Weekends and charity holidays never come out of your entitlement.`}
        actions={(
          <Button data-testid="request-leave-button" onClick={() => setForm(BLANK)}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="mr-1.5 h-4 w-4" /> Request leave
          </Button>
        )}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {data.balances.filter((b) => b.entitled > 0).slice(0, 4).map((b) => (
          <Kpi key={b.code} testId={`my-balance-${b.code}`} label={b.label} value={b.remaining}
               icon={CalendarDays} accent={b.paid ? "primary" : "amber"}
               sub={`${b.taken} taken · ${b.entitled} entitled${b.pending ? ` · ${b.pending} pending` : ""}`} />
        ))}
      </div>

      <Panel title="My requests" testId="panel-my-requests"
             kicker={`Leave year ${data.leave_year}`}>
        <Table testId="my-leave-table" head={<><Th>Reference</Th><Th>Type</Th><Th>Dates</Th>
          <Th>Days</Th><Th>Status</Th><Th>Decision</Th><Th /></>}>
          {data.requests.length === 0 && <Empty label="You have not requested any leave" colSpan={7} />}
          {data.requests.map((r) => (
            <tr key={r.leave_ref} className="border-b border-border/60"
                data-testid={`my-leave-row-${r.leave_ref}`}>
              <Td className="font-mono text-[11px]">{r.leave_ref}</Td>
              <Td>{r.leave_label}{!r.paid && " (unpaid)"}</Td>
              <Td className="whitespace-nowrap">{shortDate(r.from_date)} → {shortDate(r.to_date)}</Td>
              <Td className="font-mono text-[11px]">{r.days}</Td>
              <Td><Chip className={STATUS_STYLE[r.status]}
                        testId={`my-leave-status-${r.leave_ref}`}>
                {r.status.replace(/_/g, " ")}</Chip></Td>
              <Td className="text-[11.5px] text-muted-foreground">
                {r.decision_note || (r.status === "submitted" ? "With your manager" : "—")}
              </Td>
              <Td>
                {["submitted", "manager_approved", "approved"].includes(r.status)
                  && r.from_date > isoDay(new Date()) && (
                  <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                          data-testid={`cancel-leave-${r.leave_ref}`}
                          disabled={busy === `cancel-${r.leave_ref}`}
                          onClick={() => act(`cancel-${r.leave_ref}`,
                            () => api.post(`/hr/leave/requests/${r.leave_ref}/cancel`),
                            "Request cancelled")}>
                    Cancel
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>

      <Panel title="Charity holidays" testId="panel-my-holidays"
             kicker="These days are not counted against your leave">
        <div className="flex flex-wrap gap-1.5" data-testid="my-holiday-list">
          {(data.holidays || []).length === 0 && (
            <span className="text-[12.5px] text-muted-foreground">None recorded yet.</span>
          )}
          {(data.holidays || []).map((h, i) => (
            <Chip key={i} className="border-border bg-muted text-muted-foreground">
              {shortDate(h.date)} · {h.name}
            </Chip>
          ))}
        </div>
      </Panel>

      <Dialog open={Boolean(form)} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="request-leave-dialog">
          <DialogHeader>
            <DialogTitle>Request leave</DialogTitle>
            <DialogDescription>
              {selected
                ? `${selected.label} — ${balance?.remaining ?? 0} days left${selected.notice_days ? `, ${selected.notice_days} days' notice needed` : ""}${selected.document_required_beyond_days ? `, a document is needed beyond ${selected.document_required_beyond_days} days` : ""}.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">Type of leave</Label>
                <Select value={form.leave_type}
                        onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                  <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {data.types.map((t) => (
                      <SelectItem key={t.code} value={t.code}>
                        {t.label}{t.paid ? "" : " (unpaid)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">First day</Label>
                  <DateField value={form.from_date} clearable={false} testId="leave-from-input"
                             onChange={(v) => setForm({ ...form, from_date: v })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Last day</Label>
                  <DateField value={form.to_date} clearable={false} testId="leave-to-input"
                             onChange={(v) => setForm({ ...form, to_date: v })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">Reason</Label>
                <Textarea rows={2} value={form.reason} data-testid="leave-reason-input"
                          onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">
                    Document reference (if needed)
                  </Label>
                  <Input value={form.document_reference} data-testid="leave-document-input"
                         onChange={(e) => setForm({ ...form, document_reference: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">
                    How to reach you if urgent
                  </Label>
                  <Input value={form.contact_while_away} data-testid="leave-contact-input"
                         onChange={(e) => setForm({ ...form, contact_while_away: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={submit} disabled={busy === "submit"} data-testid="leave-submit"
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "submit" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send the request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
